/**
 * FinancialWidgets — Widgets enrichis pour vues Commerciale / Financière.
 * - OverdueInvoicesWidget : factures en retard avec relance 1-clic (modal éditable)
 * - CaEvolutionWidget    : mini chart 2 séries (actuel vs N-1) + delta %
 * - ClientsFollowupWidget : devis sans réponse > 7j + relance 1-clic (créé tâche)
 * - RecentSignaturesWidget : derniers devis signés (30j)
 * - KpiCaSigne          : KPI "CA signé" sur la période
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle, ArrowRight, CheckCircle2, Clock, FileSignature, Mail,
  Send, TrendingDown, TrendingUp, X, Loader2, Bell, Briefcase,
} from 'lucide-react';
import api from '../api/client';
import { toast } from '../hooks/use-toast';

const fmtMoney = (n) =>
  (Number(n) || 0).toLocaleString('fr-FR', { maximumFractionDigits: 0 });

// ============================================================================
// Overdue Invoices
// ============================================================================
function OverdueReminderModal({ invoice, onClose, onSent }) {
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.post(`/dashboard/widget/overdue-invoices/${invoice.id}/reminder`, { mode: 'preview' })
      .then(({ data }) => setDraft(data))
      .catch(() => toast({ title: 'Erreur', description: 'Impossible de charger l\'aperçu.' }))
      .finally(() => setLoading(false));
  }, [invoice.id]);

  const send = async () => {
    setSending(true);
    try {
      await api.post(`/dashboard/widget/overdue-invoices/${invoice.id}/reminder`, {
        mode: 'send',
        subject: draft.subject,
        body: draft.body,
        to: draft.to,
      });
      toast({ title: 'Relance envoyée', description: `Facture ${invoice.id} relancée.` });
      onSent();
      onClose();
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Envoi impossible.' });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-xl shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="overdue-reminder-modal">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h3 className="text-[15px] font-semibold text-slate-900">Relancer la facture {invoice.id}</h3>
            <p className="text-[11.5px] text-slate-500">{invoice.client} · {fmtMoney(invoice.amount)} € · {invoice.days_late} jours de retard</p>
          </div>
          <button onClick={onClose}><X className="w-4 h-4 text-slate-400 hover:text-slate-700" /></button>
        </div>
        <div className="p-5 space-y-3">
          {loading && <div className="text-center text-slate-400 py-6"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>}
          {!loading && draft && (
            <>
              <div>
                <label className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide mb-1 block">Destinataire</label>
                <input
                  type="email"
                  value={draft.to || ''}
                  onChange={(e) => setDraft({ ...draft, to: e.target.value })}
                  data-testid="reminder-to"
                  placeholder="email@client.com"
                  className="w-full text-[13px] rounded-md border border-slate-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-300"
                />
                {!draft.to && (
                  <div className="text-[10.5px] text-amber-700 mt-1">⚠ Aucun email trouvé sur la fiche client, saisissez-le manuellement.</div>
                )}
              </div>
              <div>
                <label className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide mb-1 block">Objet</label>
                <input
                  type="text"
                  value={draft.subject}
                  onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
                  data-testid="reminder-subject"
                  className="w-full text-[13px] rounded-md border border-slate-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-300"
                />
              </div>
              <div>
                <label className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide mb-1 block">Message</label>
                <textarea
                  rows={8}
                  value={draft.body}
                  onChange={(e) => setDraft({ ...draft, body: e.target.value })}
                  data-testid="reminder-body"
                  className="w-full text-[12.5px] rounded-md border border-slate-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-300 font-mono"
                />
              </div>
            </>
          )}
        </div>
        <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-end gap-2">
          <button onClick={onClose} className="text-[12.5px] rounded-md px-3 py-1.5 border border-slate-200 text-slate-700 hover:bg-slate-50">Annuler</button>
          <button
            onClick={send}
            disabled={sending || !draft?.to}
            data-testid="reminder-send-btn"
            className="text-[12.5px] rounded-md px-3 py-1.5 bg-[#4F46E5] hover:bg-[#4338CA] text-white font-semibold flex items-center gap-1.5 disabled:opacity-50"
          >
            {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            Envoyer
          </button>
        </div>
      </div>
    </div>
  );
}

export function OverdueInvoicesWidget() {
  const [data, setData] = useState({ rows: [], total_count: 0, total_amount: 0 });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const refresh = async () => {
    try {
      const { data: d } = await api.get('/dashboard/widget/overdue-invoices');
      setData(d);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); }, []);

  const quickRemind = async (inv) => {
    try {
      await api.post(`/dashboard/widget/overdue-invoices/${inv.id}/reminder`, { mode: 'send' });
      toast({ title: 'Relance envoyée', description: `Facture ${inv.id} relancée.` });
      refresh();
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Envoi impossible.' });
    }
  };

  return (
    <div data-testid="widget-overdue-invoices" className="rounded-xl bg-white border border-[#EFF0F6] p-3 flex flex-col min-h-0 h-full overflow-hidden">
      <div className="flex items-center justify-between mb-1.5 flex-shrink-0 drag-handle">
        <div className="flex items-center gap-1.5 min-w-0">
          <AlertTriangle className="w-3.5 h-3.5 text-red-500" />
          <h3 className="text-[11px] font-bold text-slate-900 uppercase tracking-wide truncate">Factures en retard</h3>
        </div>
        <div className="flex items-center gap-1 text-[10px] font-semibold">
          {data.total_count > 0 && (
            <span className="rounded bg-red-50 text-red-700 px-1.5 py-0.5 border border-red-100">
              {data.total_count} · {fmtMoney(data.total_amount)} €
            </span>
          )}
        </div>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto -mr-1 pr-1 space-y-1">
        {loading && <div className="text-center text-slate-400 py-4 text-[11px]">Chargement…</div>}
        {!loading && data.rows.length === 0 && (
          <div className="text-center text-slate-400 text-[11px] italic py-6">✨ Aucune facture en retard, bravo !</div>
        )}
        {!loading && data.rows.map((inv) => (
          <div key={inv.id} data-testid={`overdue-row-${inv.id}`} className="group flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-slate-50 border border-transparent hover:border-slate-100">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="text-[12px] font-semibold text-slate-900 truncate">{inv.client}</span>
                <span className="text-[9.5px] font-mono text-slate-400">{inv.id}</span>
              </div>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="text-[11px] font-semibold text-red-600">{fmtMoney(inv.amount)} €</span>
                <span className="text-[10px] text-red-500 inline-flex items-center gap-0.5">
                  <Clock className="w-2.5 h-2.5" /> {inv.days_late}j retard
                </span>
                {inv.reminder_count > 0 && (
                  <span className="text-[9.5px] text-slate-400">· {inv.reminder_count} relance{inv.reminder_count > 1 ? 's' : ''}</span>
                )}
              </div>
            </div>
            <div className="opacity-0 group-hover:opacity-100 flex items-center gap-1 transition-opacity">
              <button
                onClick={() => quickRemind(inv)}
                data-testid={`overdue-quick-${inv.id}`}
                title="Envoyer la relance par défaut"
                className="p-1 rounded hover:bg-indigo-100 text-[#4F46E5]"
              >
                <Send className="w-3 h-3" />
              </button>
              <button
                onClick={() => setEditing(inv)}
                data-testid={`overdue-edit-${inv.id}`}
                title="Personnaliser la relance"
                className="p-1 rounded hover:bg-slate-200 text-slate-500"
              >
                <Mail className="w-3 h-3" />
              </button>
            </div>
          </div>
        ))}
      </div>
      {editing && (
        <OverdueReminderModal
          invoice={editing}
          onClose={() => setEditing(null)}
          onSent={refresh}
        />
      )}
    </div>
  );
}

// ============================================================================
// CA Evolution (current vs previous period)
// ============================================================================
export function CaEvolutionWidget({ period = '30d' }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    api.get('/dashboard/widget/ca-evolution', { params: { period } })
      .then(({ data: d }) => setData(d))
      .finally(() => setLoading(false));
  }, [period]);

  const periodLabel = { '7d': '7 jours', '30d': '30 jours', '90d': '90 jours', '1y': '1 an' }[period] || period;
  const up = data?.delta_pct !== null && data?.delta_pct !== undefined && data.delta_pct >= 0;
  const max = useMemo(() => {
    if (!data) return 1;
    return Math.max(...(data.current_series || [0]), ...(data.previous_series || [0]), 1);
  }, [data]);

  return (
    <div data-testid="widget-ca-evolution" className="rounded-xl bg-white border border-[#EFF0F6] p-3 flex flex-col min-h-0 h-full overflow-hidden">
      <div className="flex items-center justify-between mb-1.5 flex-shrink-0 drag-handle">
        <div className="flex items-center gap-1.5 min-w-0">
          <TrendingUp className="w-3.5 h-3.5 text-indigo-500" />
          <h3 className="text-[11px] font-bold text-slate-900 uppercase tracking-wide truncate">Évolution du CA · {periodLabel}</h3>
        </div>
        {data?.delta_pct !== null && data?.delta_pct !== undefined && (
          <span className={`inline-flex items-center gap-0.5 text-[10.5px] font-semibold px-1.5 py-0.5 rounded ${up ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
            {up ? <TrendingUp className="w-2.5 h-2.5" /> : <TrendingDown className="w-2.5 h-2.5" />}
            {up ? '+' : ''}{data.delta_pct}%
          </span>
        )}
      </div>
      {loading && <div className="text-center text-slate-400 py-4 text-[11px]">Chargement…</div>}
      {!loading && data && (
        <>
          <div className="flex items-baseline gap-3 mb-2 flex-shrink-0">
            <div>
              <div className="text-[18px] font-bold text-[#4F46E5]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{fmtMoney(data.current_total)} €</div>
              <div className="text-[9.5px] text-slate-500">Période actuelle</div>
            </div>
            <div className="text-slate-300">·</div>
            <div>
              <div className="text-[14px] font-semibold text-slate-400" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{fmtMoney(data.previous_total)} €</div>
              <div className="text-[9.5px] text-slate-400">Période précédente</div>
            </div>
          </div>
          <div className="flex-1 min-h-0 flex items-end gap-1 w-full">
            {data.current_series.map((v, i) => {
              const prev = data.previous_series[i] || 0;
              const hCur = Math.max(2, (v / max) * 100);
              const hPrev = Math.max(2, (prev / max) * 100);
              return (
                <div key={i} className="flex-1 h-full flex items-end gap-px relative group">
                  <div className="flex-1 bg-slate-200 rounded-t-sm" style={{ height: `${hPrev}%` }} title={`${fmtMoney(prev)} €`} />
                  <div className="flex-1 bg-[#6366F1] rounded-t-sm" style={{ height: `${hCur}%` }} title={`${fmtMoney(v)} €`} />
                </div>
              );
            })}
          </div>
          <div className="flex items-center justify-end gap-3 mt-1.5 flex-shrink-0">
            <span className="text-[9.5px] text-slate-500 inline-flex items-center gap-1"><span className="w-2 h-2 bg-[#6366F1] rounded-sm" /> Actuel</span>
            <span className="text-[9.5px] text-slate-500 inline-flex items-center gap-1"><span className="w-2 h-2 bg-slate-300 rounded-sm" /> Précédent</span>
          </div>
        </>
      )}
    </div>
  );
}

// ============================================================================
// Clients à relancer (devis > 7j sans réponse)
// ============================================================================
export function ClientsFollowupWidget() {
  const [data, setData] = useState({ rows: [], total_count: 0, total_amount: 0 });
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      const { data: d } = await api.get('/dashboard/widget/clients-to-followup');
      setData(d);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); }, []);

  const remind = async (q) => {
    try {
      await api.post(`/dashboard/widget/clients-to-followup/${q.id}/remind`);
      toast({
        title: 'Relance programmée',
        description: `Tâche créée pour ${q.client}. Vérifiez « Mes tâches ».`,
      });
      refresh();
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Action impossible.' });
    }
  };

  return (
    <div data-testid="widget-clients-followup" className="rounded-xl bg-white border border-[#EFF0F6] p-3 flex flex-col min-h-0 h-full overflow-hidden">
      <div className="flex items-center justify-between mb-1.5 flex-shrink-0 drag-handle">
        <div className="flex items-center gap-1.5 min-w-0">
          <Briefcase className="w-3.5 h-3.5 text-amber-500" />
          <h3 className="text-[11px] font-bold text-slate-900 uppercase tracking-wide truncate">Clients à relancer</h3>
        </div>
        {data.total_count > 0 && (
          <span className="text-[10px] font-semibold rounded bg-amber-50 text-amber-700 px-1.5 py-0.5 border border-amber-100">
            {data.total_count} · {fmtMoney(data.total_amount)} €
          </span>
        )}
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto -mr-1 pr-1 space-y-1">
        {loading && <div className="text-center text-slate-400 py-4 text-[11px]">Chargement…</div>}
        {!loading && data.rows.length === 0 && (
          <div className="text-center text-slate-400 text-[11px] italic py-6">Aucun client en attente de réponse 👌</div>
        )}
        {!loading && data.rows.map((q) => (
          <div key={q.id} data-testid={`followup-row-${q.id}`} className="group flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-slate-50 border border-transparent hover:border-slate-100">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="text-[12px] font-semibold text-slate-900 truncate">{q.client}</span>
                <span className="text-[9.5px] font-mono text-slate-400">{q.id}</span>
              </div>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="text-[11px] font-semibold text-amber-700">{fmtMoney(q.amount)} €</span>
                <span className="text-[10px] text-amber-600 inline-flex items-center gap-0.5">
                  <Clock className="w-2.5 h-2.5" /> {q.days_pending}j sans réponse
                </span>
                {q.reminder_count > 0 && (
                  <span className="text-[9.5px] text-slate-400">· {q.reminder_count} relance{q.reminder_count > 1 ? 's' : ''}</span>
                )}
              </div>
            </div>
            <button
              onClick={() => remind(q)}
              data-testid={`followup-remind-${q.id}`}
              title="Créer une tâche de relance"
              className="opacity-0 group-hover:opacity-100 p-1.5 rounded-md bg-amber-100 hover:bg-amber-200 text-amber-700 transition-opacity"
            >
              <Bell className="w-3 h-3" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// Recent signatures
// ============================================================================
export function RecentSignaturesWidget() {
  const [data, setData] = useState({ rows: [], total_count: 0, total_amount: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/dashboard/widget/recent-signatures', { params: { days: 30 } })
      .then(({ data: d }) => setData(d))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="widget-recent-signatures" className="rounded-xl bg-white border border-[#EFF0F6] p-3 flex flex-col min-h-0 h-full overflow-hidden">
      <div className="flex items-center justify-between mb-1.5 flex-shrink-0 drag-handle">
        <div className="flex items-center gap-1.5 min-w-0">
          <FileSignature className="w-3.5 h-3.5 text-emerald-500" />
          <h3 className="text-[11px] font-bold text-slate-900 uppercase tracking-wide truncate">Signatures reçues · 30j</h3>
        </div>
        {data.total_count > 0 && (
          <span className="text-[10px] font-semibold rounded bg-emerald-50 text-emerald-700 px-1.5 py-0.5 border border-emerald-100">
            {data.total_count} · {fmtMoney(data.total_amount)} €
          </span>
        )}
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto -mr-1 pr-1 space-y-1">
        {loading && <div className="text-center text-slate-400 py-4 text-[11px]">Chargement…</div>}
        {!loading && data.rows.length === 0 && (
          <div className="text-center text-slate-400 text-[11px] italic py-6">Aucune signature reçue ces 30 derniers jours.</div>
        )}
        {!loading && data.rows.map((q) => {
          const dt = new Date(q.signed_at);
          const dayLabel = !Number.isNaN(dt.getTime())
            ? dt.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })
            : '—';
          return (
            <Link
              to={`/app/devis?id=${encodeURIComponent(q.id)}`}
              key={q.id}
              data-testid={`signature-row-${q.id}`}
              className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-emerald-50 border border-transparent hover:border-emerald-100 transition-colors"
            >
              <div className="w-8 h-8 rounded-md bg-emerald-100 flex items-center justify-center flex-shrink-0">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="text-[12px] font-semibold text-slate-900 truncate">{q.client}</span>
                  <span className="text-[9.5px] font-mono text-slate-400">{q.id}</span>
                </div>
                <div className="text-[10.5px] text-slate-500 truncate">{q.program} · {dayLabel}</div>
              </div>
              <span className="text-[12px] font-bold text-emerald-700 flex-shrink-0">{fmtMoney(q.amount)} €</span>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================================
// KPI Hook : CA signé sur la période
// ============================================================================
export function useKpiCaSigne(period = '30d') {
  const [kpi, setKpi] = useState(null);
  useEffect(() => {
    api.get('/dashboard/widget/quotes-signed-ca', { params: { period } })
      .then(({ data: d }) => setKpi(d))
      .catch(() => setKpi(null));
  }, [period]);
  return kpi;
}

// Re-export for ArrowRight import collateral
export { ArrowRight };
